/**
 * PLP infinite scroll with global sold-out-at-bottom ordering.
 * Available cards from later pages insert before the sold-out block;
 * sold-out cards always append after it.
 */
if (!customElements.get('sb-plp-infinite-scroll')) {
  customElements.define(
    'sb-plp-infinite-scroll',
    class SbPlpInfiniteScroll extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        this.loading = false;
        this.sentinel = this.querySelector('[data-sb-infinite-sentinel]');
        this.status = this.querySelector('[data-sb-infinite-status]');
        this.nextUrl = this.dataset.nextUrl || '';
        this.sectionId = this.dataset.sectionId || '';

        if (!this.nextUrl || !this.sectionId) return;

        // Observe the host (has padding/height). A zero-height sentinel never intersects.
        const target = this.sentinel || this;
        if (this.sentinel) {
          this.sentinel.style.minHeight = '48px';
          this.sentinel.setAttribute('aria-hidden', 'true');
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          { root: null, rootMargin: '600px 0px', threshold: 0 }
        );
        this.observer.observe(target);

        // Immediate check: IO can miss if the target is already near the viewport.
        this.scheduleCheck();
        this.onScrollCheck = () => this.scheduleCheck();
        window.addEventListener('scroll', this.onScrollCheck, { passive: true });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        if (this.onScrollCheck) {
          window.removeEventListener('scroll', this.onScrollCheck);
        }
        this._bound = false;
      }

      scheduleCheck() {
        if (this._checkQueued) return;
        this._checkQueued = true;
        requestAnimationFrame(() => {
          this._checkQueued = false;
          this.checkNearViewport();
        });
      }

      checkNearViewport() {
        if (this.loading || !this.nextUrl) return;
        const target = this.sentinel || this;
        const rect = target.getBoundingClientRect();
        if (rect.top < window.innerHeight + 600) this.loadNext();
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        if (this.status) this.status.hidden = !isLoading;
      }

      async loadNext() {
        if (this.loading || !this.nextUrl) return;
        this.setLoading(true);

        try {
          const url = new URL(this.nextUrl, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const sourceGrid = doc.getElementById('product-grid');
          const targetGrid = document.getElementById('product-grid');
          if (!sourceGrid || !targetGrid) {
            this.nextUrl = '';
            return;
          }

          const items = [...sourceGrid.querySelectorAll(':scope > .grid__item')];
          const firstSoldOut = targetGrid.querySelector(
            ':scope > .grid__item[data-sb-available="false"]'
          );

          items.forEach((item) => {
            item.querySelectorAll('.scroll-trigger').forEach((el) => {
              el.classList.add('scroll-trigger--cancel');
            });
            const available = item.getAttribute('data-sb-available') !== 'false';
            if (available && firstSoldOut) {
              targetGrid.insertBefore(item, firstSoldOut);
            } else {
              targetGrid.appendChild(item);
            }
          });

          const nextHost = doc.querySelector('sb-plp-infinite-scroll');
          this.nextUrl = nextHost?.dataset?.nextUrl || '';
          this.dataset.nextUrl = this.nextUrl;

          if (!this.nextUrl) {
            this.observer?.disconnect();
            if (this.onScrollCheck) {
              window.removeEventListener('scroll', this.onScrollCheck);
            }
            this.hidden = true;
          } else {
            this.scheduleCheck();
          }
        } catch (error) {
          console.warn('[sb-plp-infinite-scroll]', error);
          this.observer?.disconnect();
          if (this.onScrollCheck) {
            window.removeEventListener('scroll', this.onScrollCheck);
          }
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
